import {
  AddEntitiesToOrganizationRequest,
  AddEntitiesToOrganizationResponse,
  AddOrganizationRequest,
  AddOrganizationResponse,
  AddUserGroupsToOrganizationRequest,
  AddUserGroupsToOrganizationResponse,
  EditOrganizationRequest,
  EditOrganizationResponse,
  GetMyAccessibleCompaniesResponse,
  GetMyOrganizationsEntitiesResponse,
  GetMyOrganizationsResponse,
  GetOrganizationAccessResponse,
  GetOrganizationCountsByLabelResponse,
  GetOrganizationCountsByTypeResponse,
  GetOrganizationHasCompaniesResponse,
  GetOrganizationResponse,
  GetOrganizationsAssetsBreakdownResponse,
  GetOrganizationsAssetsResponse,
  GetOrganizationsResponse,
  OrganizationLabel,
  OrganizationType,
  PinCompanyResponse,
  RemoveEntitiesFromOrganizationRequest,
  UnpinCompanyResponse,
} from '../../../../common/iam/organizations/organization-types';
import { fetchOrThrow } from '../../common/fetchers/fetch-or-throw';
import { fetchApiWithError } from '../../common/fetchers/fetch-with-error';
import { appendSearchParamsToUrlPath } from '@upright/common/utils/url-management';
import { GetFundScreenerResultsResponse } from '../../../../common/model/search/fund-screener/api-types';
import { optionsToString } from '@upright/ui/api/common/util';

export async function getAllOrganizations(
  organizationType?: OrganizationType,
  name?: string,
  label?: OrganizationLabel,
) {
  const urlBase = '/organizations';
  const url = appendSearchParamsToUrlPath(urlBase, { organizationType, name, label });

  return await fetchOrThrow<GetOrganizationsResponse>(url, 'GET');
}

export async function getOrganizationCountsByType() {
  return await fetchOrThrow<GetOrganizationCountsByTypeResponse>('/organizations/counts-by-type', 'GET');
}

export async function getOrganizationCountsByLabel() {
  return await fetchOrThrow<GetOrganizationCountsByLabelResponse>('/organizations/counts-by-label', 'GET');
}

/** The Archived organizations some user groups belong to. Team only, like the list it filters. */
export async function getArchivedOrganizationsAboveUserGroups(userGroupIds: number[]) {
  const url = appendSearchParamsToUrlPath('/organizations', {
    label: OrganizationLabel.ARCHIVE,
    aboveUserGroupIds: userGroupIds.join(','),
  });
  return await fetchOrThrow<GetOrganizationsResponse>(url, 'GET');
}

export async function getOrganization(id: number) {
  return await fetchOrThrow<GetOrganizationResponse>(`/organizations/${id}`, 'GET');
}

// TODO: Deprecate and/or start to use fetchOrThrow
export async function getEntitiesOfOrganization(id: number) {
  return await fetchApiWithError<GetMyOrganizationsEntitiesResponse>(`/organizations/${id}/entities`, 'GET');
}

export async function addOrganization(payload: AddOrganizationRequest) {
  return await fetchOrThrow<AddOrganizationResponse>('/organizations', 'POST', payload);
}

export async function editOrganization(payload: EditOrganizationRequest, id: number) {
  return await fetchOrThrow<EditOrganizationResponse>(`/organizations/${id}`, 'PATCH', payload);
}

export async function addUserGroupToOrganization(payload: AddUserGroupsToOrganizationRequest) {
  return await fetchOrThrow<AddUserGroupsToOrganizationResponse>(`/organizations/add-user-group`, 'POST', payload);
}

export async function removeUserGroupsFromOrganization(organizationId: number) {
  return await fetchOrThrow<Response>(`/organizations/${organizationId}/user-group`, 'DELETE');
}

export async function getMyOrganizations() {
  return await fetchOrThrow<GetMyOrganizationsResponse>('/organizations/my-organizations', 'GET');
}

export async function getMyAccessibleCompanies() {
  return await fetchOrThrow<GetMyAccessibleCompaniesResponse>('/organizations/my-accessible-companies', 'GET');
}

/**
 * PLT-5113: pin/unpin a company on the current user's consultant dashboard.
 * Per-user; affects dashboard ordering only.
 */
export async function pinCompany(organizationId: number, companyId: string) {
  return await fetchOrThrow<PinCompanyResponse>(`/organizations/${organizationId}/companies/${companyId}/pin`, 'POST');
}

export async function unpinCompany(organizationId: number, companyId: string) {
  return await fetchOrThrow<UnpinCompanyResponse>(
    `/organizations/${organizationId}/companies/${companyId}/pin`,
    'DELETE',
  );
}

export async function getOrganizationAccess(organizationId: number) {
  return await fetchOrThrow<GetOrganizationAccessResponse>(`/organizations/${organizationId}/access`, 'GET');
}

export async function getOrganizationHasCompanies(organizationId: number) {
  return await fetchOrThrow<GetOrganizationHasCompaniesResponse>(
    `/organizations/${organizationId}/has-companies`,
    'GET',
  );
}

/**
 * PLT-4503: AI chat token usage / limit / recent activity for an org, over the
 * unified `chat_tokens` rolling window (PLT-5213).
 * Surface used by the per-org usage panel rendered next to the AI Chat
 * permission toggle in the internal org-settings view.
 *
 * Response shape mirrors `GetAiChatUsageResponse` in
 * `services/main/upserve/modules/organizations/handlers/get-ai-chat-usage.ts`.
 * No shared types path exists yet for that handler, so the type is duplicated
 * here and on the call site. If a third call site appears, extract a shared
 * type into `src/common/iam/organizations/`.
 */
export interface AiChatUsageResponse {
  organizationId: number;
  budgetedTokens: number;
  promptTokens: number;
  completionTokens: number;
  cacheCreationTokens: number;
  cacheReadTokens: number;
  windowStart: string;
  windowEnd: string;
  windowDays: number;
  limit: number;
  limitReached: boolean;
  enforced: boolean;
  recentActivity: AiChatUsageRecentActivityEntry[];
}

export interface AiChatUsageRecentActivityEntry {
  id: number;
  threadId: string;
  createdAt: string;
  status: string;
  toolsUsed: string[];
  promptTokens: number | null;
  completionTokens: number | null;
  cacheCreationTokens: number | null;
  cacheReadTokens: number | null;
  budgetedTokens: number;
}

export async function getAiChatUsage(organizationId: number) {
  return await fetchOrThrow<AiChatUsageResponse>(`/organizations/${organizationId}/ai-chat-usage`, 'GET');
}

// TODO ML: Not used anywhere?

export async function getOrganizationsAssets(organizationId: number | undefined, version: string) {
  if (organizationId === undefined) {
    throw Error('Cannot fetch organization assets with undefined organization id.');
  }
  return await fetchOrThrow<GetOrganizationsAssetsResponse>(`/organizations/${organizationId}/entities/all/${version}`);
}

export async function getOrganizationsAssetsBreakdown(
  organizationId: number | undefined,
  version: string,
  type: 'portfolios' | 'funds' | 'company-groups' | 'companies',
) {
  if (organizationId === undefined) {
    throw Error('Cannot fetch organization asset breakdown with undefined organization id.');
  }
  return await fetchOrThrow<GetOrganizationsAssetsBreakdownResponse>(
    `/organizations/${organizationId}/entities/breakdown/${version}/${type}`,
    'GET',
  );
}

export async function getOrganizationsFundAssetsBreakdown(
  organizationId: number | undefined,
  release: string,
  offset?: number,
  limit?: number,
  sortBy?: { column: string; direction: 'ASC' | 'DESC' },
) {
  if (organizationId === undefined) {
    throw Error('Cannot fetch organization asset breakdown with undefined organization id.');
  }
  const params = {
    offset,
    limit,
    sortBy: sortBy && JSON.stringify(sortBy),
  };
  return await fetchOrThrow<GetFundScreenerResultsResponse>(
    `/organizations/${organizationId}/entities/breakdown/${release}/funds${optionsToString(params)}`,
    'GET',
  );
}

/**
 * @deprecated, use the above getAllOrganizations instead
 */
export async function getAllOrganizationsDEPRECATED() {
  const temp = await fetchApiWithError<GetOrganizationsResponse>('/organizations', 'GET');
  return temp;
}
/**
 * @deprecated, use the above getOrganization instead
 */
export async function getOrganizationDEPRECATED(id: number) {
  return await fetchApiWithError<GetOrganizationResponse>(`/organizations/${id}`, 'GET');
}

/**
 * @deprecated, use the above addOrganization instead
 */
export async function addOrganizationDEPRECATED(payload: AddOrganizationRequest) {
  return await fetchApiWithError<AddOrganizationResponse>('/organizations', 'POST', payload);
}

/**
 * @deprecated, use the above deleteOrganization instead
 */
export async function deleteOrganizationDEPRECATED(id: number) {
  return await fetchApiWithError<Response>(`/organizations/${id}`, 'DELETE', {});
}

/**
 * @deprecated, use the above editOrganization instead
 */
export async function editOrganizationDEPRECATED(payload: EditOrganizationRequest, id: number) {
  return await fetchApiWithError<EditOrganizationResponse>(`/organizations/${id}`, 'PATCH', payload);
}
/**
 * @deprecated, use the above addUserGroupToOrganization instead
 */
export async function addUserGroupToOrganizationDEPRECATED(payload: AddUserGroupsToOrganizationRequest) {
  return await fetchApiWithError<AddUserGroupsToOrganizationResponse>(`/organizations/add-user-group`, 'POST', payload);
}

/**
 * @deprecated, use the above removeUserGroupsFromOrganization instead
 */
export async function removeUserGroupsFromOrganizationDEPRECATED(organizationId: number) {
  return await fetchApiWithError<Response>(`/organizations/${organizationId}/user-group`, 'DELETE');
}

/**
 * @deprecated, use the above addEntitiesToOrganization instead
 */
export async function addEntitiesToOrganizationDEPRECATED(payload: AddEntitiesToOrganizationRequest) {
  return await fetchApiWithError<AddEntitiesToOrganizationResponse>(`/organizations/add-entities`, 'POST', payload);
}

/**
 * @deprecated, use the above removeEntitiesFromOrganization instead
 */
export async function removeEntitiesFromOrganizationDEPRECATED(payload: RemoveEntitiesFromOrganizationRequest) {
  return await fetchApiWithError<Response>(`/organizations/remove-entities`, 'POST', payload);
}
